// src/MathTools/BinomialTool.js
import React, { useState, useMemo } from "react";

/* ---------- helper: factorial & combination ---------------------------- */
const factorial = (x) => (x <= 1 ? 1 : Array.from({ length: x }, (_, i) => i + 1).reduce((a, b) => a * b, 1));
const combination = (n, k) => (k > n || k < 0 ? 0 : factorial(n) / (factorial(k) * factorial(n - k)));

/* ---------- helper: normal CDF via erf approximation ------------------- */
function normalCDF(x, mu, sigma) {
  const z = (x - mu) / (sigma * Math.sqrt(2));
  // Abramowitz & Stegun 7.1.26
  const t = 1 / (1 + 0.3275911 * Math.abs(z));
  const a1 = 0.254829592,
    a2 = -0.284496736,
    a3 = 1.421413741,
    a4 = -1.453152027,
    a5 = 1.061405429;
  const erf =
    1 -
    (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) *
      Math.exp(-z * z);
  return 0.5 * (1 + Math.sign(z) * erf);
}

export default function BinomialTool({
  n: initialN = 5,
  p: initialP = 0.5,
  showPdf = true,
  showNormalOverlay = false,
  showContinuityBands = false
}) {
  const [n, setN] = useState(initialN);
  const [p, setP] = useState(initialP);

  /* ---------- 1. binomial distribution ------------------------------- */
  const { arr, maxProb } = useMemo(() => {
    let m = 0;
    const a = Array.from({ length: n + 1 }, (_, k) => {
      const prob = combination(n, k) * Math.pow(p, k) * Math.pow(1 - p, n - k);
      if (prob > m) m = prob;
      return { k, prob };
    });
    return { arr: a, maxProb: m || 1 };   // guard n = 0
  }, [n, p]);

  const totalProb = arr.reduce((s, x) => s + x.prob, 0);

  /* ---------- 2. normal overlay data ---------------------------------- */
  const normalPoints = useMemo(() => {
    if (!showNormalOverlay) return [];
    const mu = n * p;
    const sigma = Math.sqrt(n * p * (1 - p));
    return arr.map(({ k }) => {
      const area =
        normalCDF(k + 0.5, mu, sigma) -
        normalCDF(k - 0.5, mu, sigma);
      return { k, area };
    });
  }, [showNormalOverlay, arr, n, p]);

  /* ---------- 3. bar-chart dimensions --------------------------------- */
  const BAR_W = 22;
  const GAP = 6;
  const CHART_H = 120;
  const chartWidth = arr.length * (BAR_W + GAP);

  return (
    <div style={{ border: "1px solid #ccc", padding: 12, marginTop: 16 }}>
      <h4>Binomial Distribution Tool</h4>

      {/* controls */}
      <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
        <label>
          n:&nbsp;
          <input
          className="inputBox"
            type="number"
            value={n}
            min={0}
            onChange={(e) => setN(Math.max(0, +e.target.value))}
            style={{ width: 60 }}
          />
        </label>
        <label>
          p:&nbsp;
          <input
          className="inputBox"
            type="number"
            step={0.01}
            value={p}
            min={0}
            max={1}
            onChange={(e) => setP(Math.min(1, Math.max(0, +e.target.value)))}
            style={{ width: 60 }}
          />
        </label>
      </div>

      {/* ------------ 4. SVG so we can layer bars + overlay ------------- */}
      <svg width={chartWidth} height={CHART_H + 30}>
        {/* continuity bands */}
        {showContinuityBands &&
          arr.map((_, i) => (
            <rect
              key={`band-${i}`}
              x={i * (BAR_W + GAP)}
              y={0}
              width={BAR_W}
              height={CHART_H}
              fill="orange"
              fillOpacity={0.15}
            />
          ))}

        {/* bars */}
        {arr.map(({ k, prob }, i) => {
          const h = (prob / maxProb) * CHART_H;
          return (
            <g key={k}>
              <rect
                x={i * (BAR_W + GAP)}
                y={CHART_H - h}
                width={BAR_W}
                height={h}
                fill="#2196f3"
              />
              <text
                x={i * (BAR_W + GAP) + BAR_W / 2}
                y={CHART_H + 12}
                fontSize="10"
                textAnchor="middle"
              >
                k={k}
              </text>
            </g>
          );
        })}

        {/* normal overlay poly-line */}
        {showNormalOverlay && (
          <polyline
            points={normalPoints
              .map(({ k, area }, i) => {
                const h = (area / maxProb) * CHART_H;
                const x = i * (BAR_W + GAP) + BAR_W / 2;
                const y = CHART_H - h;
                return `${x},${y}`;
              })
              .join(" ")}
            fill="none"
            stroke="orange"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* list */}
      {showPdf && (
        <ul style={{ fontSize: "0.85em", paddingLeft: "1.2em", marginTop: 8 }}>
          {arr.map(({ k, prob }) => (
            <li key={k}>
              P(X={k}) = {prob.toFixed(4)} ({(prob * 100).toFixed(2)}%)
            </li>
          ))}
        </ul>
      )}

      <p style={{ fontSize: "0.8em", marginTop: 4, color: "#555" }}>
        Check sum: {totalProb.toFixed(4)} (should be ~1.0)
      </p>
    </div>
  );
}
